export type WatermarkConfig = {
  key: string;
  contextWidth: number;
  layers: number;
};

export type SampleConfig = WatermarkConfig & {
  watermarked: boolean;
  sentenceCount: number;
  seed: number;
};

export type WatermarkVerdict = 'detected' | 'weak' | 'not-detected' | 'insufficient';

export type TokenAlternative = {
  token: string;
  normalized: string;
  score: number;
  positiveBits: number;
  trials: number;
  selected: boolean;
};

export type TokenEvidence = {
  token: string;
  normalized: string;
  context: string[];
  eligible: boolean;
  score: number | null;
  positiveBits: number;
  trials: number;
  alternativeGroups: TokenAlternative[][];
};

export type WatermarkAnalysis = {
  tokenCount: number;
  eligibleTokens: number;
  positiveBits: number;
  trials: number;
  meanScore: number;
  zScore: number;
  pValue: number;
  verdict: WatermarkVerdict;
  evidence: TokenEvidence[];
};

type Choice = {
  choices: readonly string[];
};

type Segment = string | Choice;

const choice = (...choices: string[]): Choice => ({ choices });

// Curated choices keep the sample readable while standing in for a model's
// next-token distribution. The sampler chooses only among plausible words;
// it never edits a completed passage to add a signal.
const SAMPLE_SENTENCES: readonly (readonly Segment[])[] = [
  [
    'The',
    choice('review', 'assessment', 'analysis', 'evaluation'),
    choice('examined', 'studied', 'considered', 'inspected'),
    'the',
    choice('service', 'system', 'platform', 'application'),
    'boundary and found that',
    choice('several', 'multiple', 'various', 'some'),
    'controls depended on',
    choice('implicit', 'unstated', 'informal', 'unverified'),
    'assumptions.',
  ],
  [
    'Engineers',
    choice('mapped', 'traced', 'documented', 'charted'),
    'each trust transition before they',
    choice('changed', 'revised', 'adjusted', 'updated'),
    'the deployment',
    choice('design', 'plan', 'model', 'approach'),
    '.',
  ],
  [
    'The',
    choice('initial', 'first', 'early', 'opening'),
    'test used a',
    choice('small', 'limited', 'compact', 'focused'),
    'dataset so the team could',
    choice('inspect', 'review', 'examine', 'study'),
    'every result by hand.',
  ],
  [
    'A',
    choice('later', 'subsequent', 'follow-up', 'second'),
    'run introduced',
    choice('additional', 'extra', 'new', 'further'),
    'noise and measured how the detector',
    choice('responded', 'behaved', 'performed', 'reacted'),
    'under realistic conditions.',
  ],
  [
    'Instead of treating one score as',
    choice('proof', 'certainty', 'confirmation', 'fact'),
    ', the report compared distributions and stated the',
    choice('chosen', 'selected', 'configured', 'declared'),
    'false-positive threshold.',
  ],
  [
    'Longer samples generally provide',
    choice('stronger', 'clearer', 'better', 'greater'),
    'evidence because the detector can aggregate',
    choice('many', 'numerous', 'repeated', 'additional'),
    'tiny token-level observations.',
  ],
  [
    'Short factual passages contain',
    choice('fewer', 'limited', 'scarcer', 'reduced'),
    'opportunities for harmless variation, which makes any quantitative conclusion',
    choice('weaker', 'harder', 'uncertain', 'fragile'),
    '.',
  ],
  [
    'The team kept the key',
    choice('separate', 'distinct', 'isolated', 'apart'),
    'from the sample and recorded the exact',
    choice('settings', 'parameters', 'configuration', 'options'),
    'used for every experiment.',
  ],
  [
    'When the detector used a mismatched key, the apparent pattern',
    choice('returned', 'fell', 'moved', 'settled'),
    'toward the',
    choice('expected', 'ordinary', 'baseline', 'normal'),
    'null distribution.',
  ],
  [
    'Minor edits',
    choice('reduced', 'weakened', 'diluted', 'lowered'),
    'the signal gradually, while extensive rewriting replaced enough token choices to make the original evidence',
    choice('difficult', 'hard', 'unlikely', 'impractical'),
    'to recover.',
  ],
  [
    'Formatting changes had',
    choice('little', 'minimal', 'limited', 'negligible'),
    'effect because the experiment measured lexical choices rather than hidden',
    choice('characters', 'spacing', 'markup', 'metadata'),
    '.',
  ],
  [
    'The final dashboard displayed both the observed score and the amount of',
    choice('usable', 'eligible', 'available', 'meaningful'),
    'evidence so readers could',
    choice('interpret', 'understand', 'evaluate', 'judge'),
    'the result in context.',
  ],
  [
    'Every computation ran',
    choice('locally', 'privately', 'entirely', 'directly'),
    'in the browser, and the page did not',
    choice('retain', 'store', 'upload', 'transmit'),
    "the visitor's text or key.",
  ],
  [
    'The implementation used a deliberately',
    choice('simple', 'small', 'readable', 'compact'),
    'hash construction so visitors could follow the mechanism without mistaking it for a',
    choice('production', 'provider', 'commercial', 'deployed'),
    'detector.',
  ],
  [
    'A real-world integration would require the vendor to publish a',
    choice('supported', 'documented', 'stable', 'verified'),
    'interface and define how outputs should be',
    choice('calibrated', 'interpreted', 'reported', 'validated'),
    '.',
  ],
  [
    'Until that interface exists, the lab keeps vendor detection',
    choice('disabled', 'unavailable', 'separate', 'inactive'),
    'and labels its own experiment as a',
    choice('simulation', 'demonstration', 'model', 'prototype'),
    '.',
  ],
  [
    'Users can change the key and immediately observe that the same passage produces a',
    choice('different', 'new', 'changed', 'separate'),
    'sequence of',
    choice('scores', 'measurements', 'values', 'results'),
    '.',
  ],
  [
    'This makes the central lesson',
    choice('visible', 'concrete', 'clear', 'tangible'),
    ': a key-specific check is not a general-purpose judgment about whether prose',
    choice('sounds', 'appears', 'reads', 'feels'),
    'machine-written.',
  ],
  [
    'Careful experiments therefore preserve the distinction between',
    choice('authorship', 'style', 'origin', 'provenance'),
    'classification and',
    choice('keyed', 'statistical', 'configured', 'watermark'),
    'verification.',
  ],
  [
    'The result is a',
    choice('practical', 'hands-on', 'useful', 'focused'),
    "way to probe detection thresholds without claiming access to any provider's",
    choice('private', 'secret', 'production', 'internal'),
    'key.',
  ],
  [
    'Repeating the exercise with control text helps estimate how often random variation creates an apparently',
    choice('unusual', 'strong', 'notable', 'elevated'),
    'score under a',
    choice('particular', 'selected', 'given', 'specific'),
    'threshold.',
  ],
  [
    'Together, these comparisons turn an invisible mathematical idea into an',
    choice('observable', 'interactive', 'measurable', 'accessible'),
    'experiment with',
    choice('explicit', 'visible', 'documented', 'clear'),
    'limitations.',
  ],
  [
    'The lab encourages readers to test',
    choice('multiple', 'several', 'different', 'varied'),
    'keys, lengths, and strengths before drawing any',
    choice('broad', 'general', 'strong', 'confident'),
    'conclusion.',
  ],
  [
    'Good measurement begins with a precise claim, a known null hypothesis, and a result that another person can',
    choice('reproduce', 'repeat', 'verify', 'audit'),
    'using the same',
    choice('inputs', 'parameters', 'settings', 'configuration'),
    '.',
  ],
];

export const WATERMARK_VOCABULARY_GROUPS: readonly (readonly string[])[] =
  SAMPLE_SENTENCES.flatMap((sentence) =>
    sentence.flatMap((segment) => (typeof segment === 'string' ? [] : [segment.choices]))
  );

const ELIGIBLE_TOKENS = new Set(
  WATERMARK_VOCABULARY_GROUPS.flatMap((group) => group.map(normalizeToken))
);

const VOCABULARY_GROUPS_BY_TOKEN = new Map<string, readonly (readonly string[])[]>();
for (const group of WATERMARK_VOCABULARY_GROUPS) {
  for (const token of group) {
    const normalized = normalizeToken(token);
    const existing = VOCABULARY_GROUPS_BY_TOKEN.get(normalized) ?? [];
    if (!existing.includes(group)) {
      VOCABULARY_GROUPS_BY_TOKEN.set(normalized, [...existing, group]);
    }
  }
}

const WORD_PATTERN = /[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*/gu;

function hash32(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
    hash ^= hash >>> 13;
  }
  // MurmurHash3 finalizer. Without it, inputs differing only in the last few
  // characters (bracket draw indices, tie-break counters) leave the high bits
  // nearly unchanged, which collapses bracket sampling and skews the null.
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  return hash >>> 0;
}

function randomUnit(seed: string): number {
  return hash32(seed) / 0x1_0000_0000;
}

function normalizeToken(token: string): string {
  return token.normalize('NFKC').toLocaleLowerCase('en-US');
}

export function tokenizeWords(text: string): string[] {
  return text.match(WORD_PATTERN) ?? [];
}

function gBit(
  key: string,
  context: readonly string[],
  token: string,
  layer: number
): 0 | 1 {
  const material = `${key}\u241f${layer}\u241f${context.join('\u241e')}\u241f${normalizeToken(token)}`;
  return (hash32(material) & 1) as 0 | 1;
}

function sampleCandidate(
  candidates: readonly string[],
  seed: number,
  position: number,
  draw: number
): string {
  const value = randomUnit(`${seed}\u241f${position}\u241f${draw}`);
  return candidates[
    Math.min(candidates.length - 1, Math.floor(value * candidates.length))
  ];
}

function selectTournamentCandidate(
  candidates: readonly string[],
  context: readonly string[],
  config: SampleConfig,
  position: number
): string {
  if (!config.watermarked) {
    return sampleCandidate(candidates, config.seed, position, 0);
  }

  // Each curated option is a plausible continuation. A depth-m tournament
  // starts with 2^m independent draws. Round m uses its own keyed g-bit;
  // candidates with g=1 beat candidates with g=0, and ties are randomized.
  // This mirrors the shape of tournament sampling while using a tiny curated
  // distribution instead of a language model's token probabilities.
  // A real tournament sampler draws from a model's full vocabulary, where two
  // draws rarely collide. Sampling i.i.d. from this lab's four curated options
  // pairs identical words often, and those ties resolve by coin flip and carry
  // no keyed signal. Filling the bracket with whole copies of the candidate
  // set before topping up with random draws keeps the draws balanced and the
  // pairings informative, then a keyed shuffle randomizes the seeding.
  const bracketSize = 2 ** config.layers;
  const bracket: string[] = [];
  while (bracket.length + candidates.length <= bracketSize) {
    bracket.push(...candidates);
  }
  for (let draw = bracket.length; draw < bracketSize; draw += 1) {
    bracket.push(sampleCandidate(candidates, config.seed, position, draw));
  }
  for (let index = bracket.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(
      randomUnit(`${config.seed}\u241f${position}\u241fshuffle\u241f${index}`) *
        (index + 1)
    );
    [bracket[index], bracket[swap]] = [bracket[swap], bracket[index]];
  }
  let pool = bracket;

  for (let round = 0; pool.length > 1; round += 1) {
    const winners: string[] = [];
    for (let index = 0; index < pool.length; index += 2) {
      const left = pool[index];
      const right = pool[index + 1];
      const leftScore = gBit(config.key, context, left, round);
      const rightScore = gBit(config.key, context, right, round);

      if (leftScore !== rightScore) {
        winners.push(leftScore > rightScore ? left : right);
      } else {
        const pickRight = randomUnit(
          `${config.seed}\u241f${position}\u241f${round}\u241f${index}\u241ftie`
        );
        winners.push(pickRight >= 0.5 ? right : left);
      }
    }
    pool = winners;
  }

  return pool[0];
}

function appendWords(history: string[], text: string): void {
  for (const token of tokenizeWords(text)) {
    history.push(normalizeToken(token));
  }
}

function renderSentence(
  segments: readonly Segment[],
  config: SampleConfig,
  history: string[],
  position: { value: number }
): string {
  const rendered: string[] = [];
  for (const segment of segments) {
    if (typeof segment === 'string') {
      rendered.push(segment);
      appendWords(history, segment);
      continue;
    }

    const context = history.slice(-config.contextWidth);
    const selected = selectTournamentCandidate(
      segment.choices,
      context,
      config,
      position.value
    );
    position.value += 1;
    rendered.push(selected);
    appendWords(history, selected);
  }

  return rendered.join(' ').replace(/\s+([.,;:!?])/g, '$1');
}

export function generateWatermarkSample(config: SampleConfig): string {
  validateConfig(config);
  const history: string[] = [];
  const position = { value: 0 };
  const sentences: string[] = [];
  const count = Math.max(1, Math.min(config.sentenceCount, SAMPLE_SENTENCES.length));

  for (let index = 0; index < count; index += 1) {
    const sentenceIndex =
      (index + (config.seed % SAMPLE_SENTENCES.length)) % SAMPLE_SENTENCES.length;
    sentences.push(
      renderSentence(SAMPLE_SENTENCES[sentenceIndex], config, history, position)
    );
  }

  const paragraphs: string[] = [];
  for (let index = 0; index < sentences.length; index += 4) {
    paragraphs.push(sentences.slice(index, index + 4).join(' '));
  }
  return paragraphs.join('\n\n');
}

function validateConfig(config: WatermarkConfig): void {
  if (!config.key.trim()) throw new Error('Enter a key before running the experiment.');
  if (
    !Number.isInteger(config.contextWidth) ||
    config.contextWidth < 1 ||
    config.contextWidth > 8
  ) {
    throw new Error('Context width must be an integer from 1 to 8.');
  }
  if (!Number.isInteger(config.layers) || config.layers < 1 || config.layers > 6) {
    throw new Error('Layers must be an integer from 1 to 6.');
  }
}

// Complementary error function approximation from Numerical Recipes. The
// detector uses erfc(z / sqrt(2)) / 2 for a one-sided normal-tail probability.
function erfc(value: number): number {
  const z = Math.abs(value);
  const t = 1 / (1 + z / 2);
  const result =
    t *
    Math.exp(
      -z * z -
        1.26551223 +
        t *
          (1.00002368 +
            t *
              (0.37409196 +
                t *
                  (0.09678418 +
                    t *
                      (-0.18628806 +
                        t *
                          (0.27886807 +
                            t *
                              (-1.13520398 +
                                t *
                                  (1.48851587 + t * (-0.82215223 + t * 0.17087277))))))))
    );
  return value >= 0 ? result : 2 - result;
}

export function analyzeWatermark(
  text: string,
  config: WatermarkConfig
): WatermarkAnalysis {
  validateConfig(config);
  const tokens = tokenizeWords(text);
  const normalized = tokens.map(normalizeToken);
  const seenContexts = new Set<string>();
  const evidence: TokenEvidence[] = [];
  let positiveBits = 0;
  let trials = 0;
  let eligibleTokens = 0;

  for (let index = 0; index < tokens.length; index += 1) {
    if (index < config.contextWidth || !ELIGIBLE_TOKENS.has(normalized[index])) {
      evidence.push({
        token: tokens[index],
        normalized: normalized[index],
        context: [],
        eligible: false,
        score: null,
        positiveBits: 0,
        trials: 0,
        alternativeGroups: [],
      });
      continue;
    }

    const context = normalized.slice(index - config.contextWidth, index);
    const contextId = context.join('\u241e');
    if (seenContexts.has(contextId)) {
      evidence.push({
        token: tokens[index],
        normalized: normalized[index],
        context: [],
        eligible: false,
        score: null,
        positiveBits: 0,
        trials: 0,
        alternativeGroups: [],
      });
      continue;
    }
    seenContexts.add(contextId);

    let tokenPositiveBits = 0;
    for (let layer = 0; layer < config.layers; layer += 1) {
      tokenPositiveBits += gBit(config.key, context, normalized[index], layer);
    }
    const alternativeGroups = (
      VOCABULARY_GROUPS_BY_TOKEN.get(normalized[index]) ?? []
    ).map((group) =>
      group.map((candidate) => {
        let candidatePositiveBits = 0;
        for (let layer = 0; layer < config.layers; layer += 1) {
          candidatePositiveBits += gBit(config.key, context, candidate, layer);
        }
        return {
          token: candidate,
          normalized: normalizeToken(candidate),
          score: candidatePositiveBits / config.layers,
          positiveBits: candidatePositiveBits,
          trials: config.layers,
          selected: normalizeToken(candidate) === normalized[index],
        };
      })
    );
    positiveBits += tokenPositiveBits;
    trials += config.layers;
    eligibleTokens += 1;
    evidence.push({
      token: tokens[index],
      normalized: normalized[index],
      context: [...context],
      eligible: true,
      score: tokenPositiveBits / config.layers,
      positiveBits: tokenPositiveBits,
      trials: config.layers,
      alternativeGroups,
    });
  }

  const meanScore = trials > 0 ? positiveBits / trials : 0.5;
  const zScore =
    trials > 0 ? (positiveBits - trials * 0.5) / Math.sqrt(trials * 0.25) : 0;
  const pValue = Math.min(1, Math.max(0, erfc(zScore / Math.SQRT2) / 2));

  // Thresholds are calibrated to the evidence a full generated sample carries
  // (roughly 200 trials): z >= 3 is a one-sided p of about 0.0013, which the
  // null distribution essentially never reaches, while z >= 4 would demand a
  // per-trial rate the tournament cannot sustain at this sample size.
  let verdict: WatermarkVerdict;
  if (trials < 80) verdict = 'insufficient';
  else if (zScore >= 3) verdict = 'detected';
  else if (zScore >= 2) verdict = 'weak';
  else verdict = 'not-detected';

  return {
    tokenCount: tokens.length,
    eligibleTokens,
    positiveBits,
    trials,
    meanScore,
    zScore,
    pValue,
    verdict,
    evidence,
  };
}

export function keyFingerprint(key: string): string {
  if (!key) return 'No key selected';
  return hash32(`fingerprint\u241f${key}`).toString(16).padStart(8, '0').toUpperCase();
}
